import { Modal } from 'react-bootstrap'
import { useEffect, useRef, useState } from 'react'
import { useInfiniteQuery } from 'react-query'
import { CiSearch } from 'react-icons/ci'
import posthog from 'posthog-js'
import classNames from 'classnames'
import InfiniteScroll from 'react-infinite-scroll-component'
import DotsLoading from 'components/loadings/dotsLoading'
import AcceptIcon from 'assets/images/loans/accepted.svg'
import { getMonyPoolsDetailUsers } from 'services/fund'

export const MAX_GUARANTORS = 2

export interface IGuarantorOption {
    poolship_id: number
    name: string
}

interface IGuarantorPickerProps {
    show: boolean
    onHide: () => void
    moneypoolId?: string | number
    /** already-picked guarantors; the modal opens with these ticked */
    value: Array<IGuarantorOption>
    onChange: (guarantors: Array<IGuarantorOption>) => void
    /**
     * The borrower — never offered as an option, since nobody can guarantee
     * their own loan (the backend rejects it too).
     */
    excludePoolshipId?: number
    /**
     * The borrower's ضامن عضویت, pinned to the top of the list. Funds record
     * this when a member joins and it is overwhelmingly the same person who
     * backs their loan, so surfacing it saves scrolling a 900-member list.
     */
    suggested?: IGuarantorOption | null
    title?: string
}

export default function GuarantorPickerModal({
    show,
    onHide,
    moneypoolId,
    value,
    onChange,
    excludePoolshipId,
    suggested,
    title,
}: IGuarantorPickerProps) {
    const [selected, setSelected] = useState<Array<IGuarantorOption>>(value)
    const [searchInput, setSearchInput] = useState<string>('')
    const [searchQuery, setSearchQuery] = useState<string>(' ')

    // `/poolships/` is cursor-paginated. Fetching it once listed only the first
    // page, so on any fund past ~20 members most people could not be picked as
    // a guarantor at all unless you already knew the name and searched for it.
    const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery(
        ['guarantor-picker-users', moneypoolId, searchQuery.trim()],
        ({ pageParam = 1 }) =>
            getMonyPoolsDetailUsers(
                pageParam,
                moneypoolId === undefined ? undefined : String(moneypoolId),
                searchQuery.trim()
            ),
        {
            getNextPageParam: (lastPage, allPages) => {
                const lastRecord = allPages[allPages.length - 1]?.next || ''
                return lastRecord
                    ? new URL(lastRecord).searchParams.get('cursor')
                    : undefined
            },
            enabled: show && !!moneypoolId,
        }
    )

    // Whether this open involved a search at all. Kept as a ref rather than
    // read off `searchQuery` at confirm time: someone who searches, picks, and
    // then clears the box has still used the search to find their person.
    const usedSearch = useRef(false)

    // Re-sync whenever the modal is re-opened: the parent is the source of
    // truth, and a cancelled edit must not leak into the next open.
    useEffect(() => {
        if (show) {
            setSelected(value)
            usedSearch.current = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show])

    const runSearch = () => {
        if (searchInput.trim()) usedSearch.current = true
        setSearchQuery(searchInput)
    }

    useEffect(() => {
        if (searchInput === '') {
            setSearchQuery('')
        }
    }, [searchInput])

    const isSelected = (poolshipId: number) =>
        selected.some((item) => item.poolship_id === poolshipId)

    const toggle = (option: IGuarantorOption) => {
        if (isSelected(option.poolship_id)) {
            setSelected(
                selected.filter(
                    (item) => item.poolship_id !== option.poolship_id
                )
            )
            return
        }
        if (selected.length >= MAX_GUARANTORS) {
            // Drop the oldest pick rather than silently ignoring the tap —
            // with a 2-slot limit, "replace" is what people actually mean.
            setSelected([...selected.slice(1), option])
            return
        }
        setSelected([...selected, option])
    }

    const options: Array<IGuarantorOption> = []
    if (suggested && suggested.poolship_id !== excludePoolshipId) {
        options.push(suggested)
    }
    data?.pages
        ?.flatMap((page) => page.results ?? [])
        .forEach((item: any) => {
            if (!item.is_active) return
            if (item.id === excludePoolshipId) return
            if (options.some((option) => option.poolship_id === item.id)) return
            options.push({
                poolship_id: item.id,
                name: `${item.member?.first_name ?? ''} ${
                    item.member?.last_name ?? ''
                }`.trim(),
            })
        })

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="sm"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <div className="py-3 px-2">
                <p className="text-center text-sm">
                    {title ?? 'ضامن وام را انتخاب کنید'}
                </p>
                <p className="text-center text-xs text-gray-4 mt-1">
                    حداکثر {MAX_GUARANTORS} ضامن — انتخاب اختیاری است
                </p>
                <hr className="my-2" />

                {/* the row was pinned to h-[25px] while the input's own padding
                    made it taller, so it spilled over the first member */}
                <div className="flex items-center mb-1">
                    <input
                        type="text"
                        className="w-full outline-0 border border-green-1 rounded-full px-2 py-1 ml-3"
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                runSearch()
                            }
                        }}
                        value={searchInput}
                        placeholder="جستجو..."
                    />
                    <CiSearch
                        className="text-green-1 w-8 shrink-0 cursor-pointer"
                        size={22}
                        onClick={runSearch}
                    />
                </div>

                <div
                    id="guarantor-picker-members"
                    className="h-72 overflow-y-auto"
                >
                    {isLoading ? (
                        <DotsLoading />
                    ) : (
                        <InfiniteScroll
                            next={fetchNextPage}
                            hasMore={Boolean(hasNextPage)}
                            dataLength={options.length}
                            loader={<DotsLoading />}
                            scrollableTarget="guarantor-picker-members"
                        >
                            {/* only "nothing here" once the pages are exhausted:
                                a page whose rows are all filtered out (inactive,
                                or the borrower themselves) is not an empty list */}
                            {options.length === 0 && !hasNextPage && (
                                <p className="text-center text-sm text-gray-4 mt-8">
                                    عضوی برای انتخاب پیدا نشد
                                </p>
                            )}
                            {options.map((option) => (
                                <div
                                    key={option.poolship_id}
                                    className={classNames(
                                        'my-3 flex justify-between items-center px-2 cursor-pointer py-1',
                                        isSelected(option.poolship_id) &&
                                            'rounded-full border-1 border-green-1'
                                    )}
                                    onClick={() => toggle(option)}
                                >
                                    {/* The badge used to sit INSIDE the name's
                                        <p> with nothing between them. With no
                                        separating character the browser shapes
                                        straight through the element boundary,
                                        so «فاطمه شاکری» rendered with its final
                                        ی joined onto the badge — «شاکریـ» next
                                        to «ـضامن عضویت». Making the name and the
                                        badge sibling flex items puts them in
                                        separate boxes, which ends the shaping
                                        run and leaves both in their real forms. */}
                                    <div className="flex min-w-0 items-center gap-2">
                                        <p
                                            className={classNames(
                                                'text-sm truncate',
                                                isSelected(
                                                    option.poolship_id
                                                ) && 'text-green-1'
                                            )}
                                        >
                                            {option.name}
                                        </p>
                                        {suggested?.poolship_id ===
                                            option.poolship_id && (
                                            <span className="shrink-0 text-[10px] text-blue-1 border border-blue-1 rounded-full px-2 py-[1px]">
                                                ضامن عضویت
                                            </span>
                                        )}
                                    </div>
                                    {isSelected(option.poolship_id) && (
                                        <img
                                            src={AcceptIcon}
                                            alt="selected"
                                            className="shrink-0"
                                        />
                                    )}
                                </div>
                            ))}
                        </InfiniteScroll>
                    )}
                </div>

                <hr className="my-2" />
                <div className="flex justify-between px-4">
                    <p
                        className="text-gray-4 cursor-pointer"
                        onClick={() => {
                            // «بدون ضامن» after picking somebody is a
                            // deliberate undo, not the same act as opening the
                            // list and closing it empty-handed
                            posthog.capture('loan_guarantor_declined', {
                                had_selection: selected.length > 0,
                                has_suggested: !!suggested,
                            })
                            setSelected([])
                            onChange([])
                            onHide()
                        }}
                    >
                        بدون ضامن
                    </p>
                    <p
                        className="text-green-1 cursor-pointer"
                        onClick={() => {
                            // counts and flags only — the modal is a list of
                            // members by name, and none of that belongs here
                            posthog.capture(
                                'loan_guarantor_selection_confirmed',
                                {
                                    count: selected.length,
                                    used_membership_guarantor: selected.some(
                                        (item) =>
                                            item.poolship_id ===
                                            suggested?.poolship_id
                                    ),
                                    used_search: usedSearch.current,
                                }
                            )
                            onChange(selected)
                            onHide()
                        }}
                    >
                        تایید
                    </p>
                </div>
            </div>
        </Modal>
    )
}
